import type { BridgeChartApi, BridgeChartInspection } from "../types/public";
import type { BridgeChartInput, BridgeChartIntelligenceMarker } from "../types/data";
import { normalizeIntelligenceMarkers } from "../overlays/markers";

type NativeEvidenceMarker = Omit<BridgeChartIntelligenceMarker, "eventProvenance">;
const kinds = ["Strategy", "Backtest", "WalkForward", "CompositeAi", "Codal", "MaterialRisk"];

/** Enhances the original SSR evidence only. A new data/basis assignment needs new panels. */
export function bindWorkspaceEvidence(container: HTMLElement, chart: BridgeChartApi, source: BridgeChartInput): () => void {
  const inspector = container.querySelector<HTMLElement>("[data-chart-evidence-inspector]");
  const select = inspector?.querySelector<HTMLSelectElement>("[data-chart-evidence-select]");
  const open = inspector?.querySelector<HTMLButtonElement>("[data-chart-evidence-open]");
  const status = inspector?.querySelector<HTMLElement>("[data-chart-evidence-status]");
  const rail = container.querySelector<HTMLElement>("[data-testid='bridge-chart-evidence-panels']");
  const drawer = container.querySelector<HTMLDetailsElement>(".bridge-chart-information");
  if (container.dataset.chartContext !== "workspace" || !inspector || !select || !open || !status || !rail || !drawer) return () => {};

  const original = chart.data, locale = container.lang.startsWith("fa") ? "fa" : "en";
  const panels = new Map<string, HTMLDetailsElement>();
  const duplicatePanels = new Set<string>();
  rail.querySelectorAll<HTMLDetailsElement>("details[data-marker-id]").forEach(panel => {
    const id = panel.dataset.markerId;
    if (!id) return;
    if (panels.has(id)) duplicatePanels.add(id);
    else panels.set(id, panel);
  });
  duplicatePanels.forEach(id => panels.delete(id));
  const matchesPanel = (marker: NativeEvidenceMarker, panel: HTMLDetailsElement) =>
    panel.dataset.markerId === marker.markerId && panel.dataset.markerDate === marker.date && panel.dataset.markerKind === marker.kind
    && panel.dataset.markerLabelEn === marker.labelEn && panel.dataset.markerLabelFa === marker.labelFa
    && panel.dataset.markerSourceType === marker.source.sourceType && panel.dataset.markerSourceId === marker.source.sourceId
    && panel.dataset.markerSourceRevision === marker.source.sourceRevision && panel.dataset.markerRunId === (marker.source.runId || "")
    && panel.dataset.markerEvidenceId === marker.evidence.evidenceId;
  const markersByDate = new Map<string, NativeEvidenceMarker[]>();
  // Use the exact original SSR contract, not a fabricated temporal overlay stamp.
  for (const marker of nativeEvidence(source.intelligenceMarkers || [])) {
    const panel = panels.get(marker.markerId);
    // Exact date equality; never borrow evidence from a nearby trading session.
    if (!panel || !matchesPanel(marker, panel)) continue;
    const markers = markersByDate.get(marker.date) || [];
    markers.push(marker);
    markersByDate.set(marker.date, markers);
  }
  let disposed = false, contextValid = true;
  let inspection: { date: string; index: number } | null = null;
  let candidates = new Map<string, NativeEvidenceMarker>();
  let opened: HTMLDetailsElement | null = null;
  const originalRailHidden = rail.hidden;
  const closePanel = () => { if (opened) opened.open = false; opened = null; };
  const clear = () => {
    closePanel(); inspection = null; candidates.clear(); select.replaceChildren();
    select.disabled = true; open.disabled = true;
    status.textContent = contextValid ? inspector.dataset.evidencePrompt || "" : inspector.dataset.evidenceUnavailable || "";
  };
  const currentAssignment = () => chart.data.bars === original.bars
    && chart.data.intelligenceMarkers === original.intelligenceMarkers
    && chart.data.symbol === original.symbol && chart.data.timeframe === original.timeframe;
  const validInspection = () => !!inspection && contextValid && currentAssignment()
    && inspection.index >= chart.viewport.start && inspection.index <= chart.viewport.end
    && chart.data.bars[inspection.index]?.date === inspection.date;
  const inspect = (payload: unknown) => {
    if (disposed) return;
    const event = payload as BridgeChartInspection;
    if (event.state === "Cleared") {
      if (event.reason === "PriceBasis") { contextValid = false; rail.hidden = true; }
      clear(); return;
    }
    clear(); inspection = { date: event.date, index: event.index };
    if (!validInspection()) { clear(); return; }
    const markers = markersByDate.get(event.date) || [];
    candidates = new Map(markers.map(marker => [marker.markerId, marker]));
    for (const marker of markers) {
      const option = container.ownerDocument.createElement("option");
      option.value = marker.markerId;
      option.textContent = `${locale === "fa" ? marker.labelFa : marker.labelEn} · ${marker.source.sourceType}/${marker.source.sourceId}`;
      select.appendChild(option);
    }
    select.disabled = open.disabled = markers.length === 0;
    if (markers.length) select.value = markers[0].markerId;
    status.textContent = `${event.date} · ${markers.length || inspector.dataset.evidenceEmpty || ""}`;
  };
  const activate = () => {
    if (disposed || !validInspection()) { clear(); return; }
    const marker = candidates.get(select.value), panel = marker && panels.get(marker.markerId);
    if (!marker || !panel || marker.date !== inspection!.date || !matchesPanel(marker, panel)) { clear(); return; }
    const summary = panel.querySelector<HTMLElement>("summary");
    if (!summary) { clear(); return; }
    closePanel(); drawer.open = true; panel.open = true; opened = panel;
    summary.focus();
  };
  const refresh = () => {
    if (!currentAssignment()) { contextValid = false; rail.hidden = true; }
    clear();
  };
  open.addEventListener("click", activate);
  const offInspection = chart.on("inspection", inspect), offData = chart.on("data", refresh);
  const dispose = () => {
    if (disposed) return;
    disposed = true; clear(); inspector.hidden = true; rail.hidden = originalRailHidden;
    open.removeEventListener("click", activate); offInspection(); offData(); offDestroy();
  };
  const offDestroy = chart.on("destroy", dispose);
  inspector.hidden = false; clear();
  return dispose;
}

function nativeEvidence(rows: readonly BridgeChartIntelligenceMarker[]): NativeEvidenceMarker[] {
  const ids = new Map<string, number>();
  for (const row of rows) {
    const id = String(row?.markerId || "").trim();
    ids.set(id, (ids.get(id) || 0) + 1);
  }
  const text = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
  return rows.filter(row => row && ids.get(String(row.markerId || "").trim()) === 1
    && kinds.includes(row.kind) && text(row.markerId) && /^\d{4}-\d{2}-\d{2}$/.test(row.date)
    && text(row.direction) && text(row.labelEn) && text(row.labelFa)
    && text(row.source?.sourceType) && text(row.source?.sourceId) && text(row.source?.sourceRevision)
    && text(row.evidence?.evidenceId) && text(row.evidence?.summary)
    && Array.isArray(row.evidence?.provenance) && row.evidence.provenance.length > 0 && row.evidence.provenance.every(text)
    // Present temporal claims must still satisfy the existing strict contract. Missing claims remain absent.
    && (!Object.prototype.hasOwnProperty.call(row, "eventProvenance") || normalizeIntelligenceMarkers([row]).length === 1))
    .sort((a, b) => a.date.localeCompare(b.date) || kinds.indexOf(a.kind) - kinds.indexOf(b.kind)
      || a.source.sourceId.localeCompare(b.source.sourceId) || a.markerId.localeCompare(b.markerId));
}
